import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { RadioTower, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { timeAgo, fmtDate, formatApiErrorDetail } from "../lib/api";
import { StatusBadge, TypeBadge, PriorityBadge } from "../components/badges";

const StatTile = ({ label, value, accent, testid }) => (
  <div data-testid={testid} className="border border-white/10 bg-[#121212]/90 p-5 backdrop-blur-sm transition-shadow duration-200 hover:shadow-[0_0_16px_rgba(0,229,255,0.08)]">
    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">{label}</div>
    <div className={`mt-2 font-mono text-3xl font-bold ${accent || "text-white"}`}>{value}</div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [recos, setRecos] = useState([]);
  const [pinging, setPinging] = useState({});
  const [live, setLive] = useState(false);

  const load = useCallback(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
    api.get("/recommendations?limit=8").then(({ data }) => setRecos(data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const es = new EventSource(`${process.env.REACT_APP_BACKEND_URL}/api/stream/events`, { withCredentials: true });
    es.onopen = () => setLive(true);
    es.onerror = () => setLive(false);
    es.onmessage = (e) => {
      let evt;
      try { evt = JSON.parse(e.data); } catch { return; }
      if (evt.event === "connected") { setLive(true); return; }
      const d = evt.data || {};
      if (evt.event === "alert") {
        const text = `[${d.category}] ${d.message} — ${d.source}`;
        if (d.priority === "high" || d.priority === "critical") toast.error(text, { duration: 10000 });
        else toast.warning(text, { duration: 8000 });
        load();
      } else if (evt.event === "recommendation") {
        const p = d.payload || {};
        toast.info(`Oracle · ${p.author || d.source} — ${p.title || "nouvelle recommandation"}`, { duration: 8000 });
        load();
      } else if (evt.event === "brain_signal") {
        const label = d.kind === "probleme" ? "Problème" : d.kind === "opportunite" ? "Opportunité" : "Info";
        const text = `Cerveau · ${label} — ${d.title}`;
        if (d.severity === "critique") toast.error(text, { duration: 12000 });
        else if (d.kind === "probleme") toast.warning(text, { duration: 10000 });
        else if (d.kind === "opportunite") toast.success(text, { duration: 10000 });
        else toast.info(text, { duration: 8000 });
      } else if (evt.event === "decision") {
        toast.info(`Conseil · ${d.source} — ${d.title || "nouvelle décision"}${d.made_by ? ` (${d.made_by})` : ""}`, { duration: 10000 });
        load();
      } else if (evt.event === "order_proposal") {
        toast.warning(`Bureau des ordres · ${d.project} — signature requise : ${d.ordre?.slice(0, 90)}`, { duration: 15000 });
      } else if (evt.event === "order_update") {
        if (d.status === "escalated") toast.error(`ESCALADE · ${d.project} — ${d.detail || d.ordre?.slice(0, 90)}`, { duration: 15000 });
        else toast.info(`Ordre · ${d.project} a répondu : ${d.decision}`, { duration: 10000 });
      }
    };
    return () => es.close();
  }, [load]);

  const ping = async (pid) => {
    setPinging((p) => ({ ...p, [pid]: true }));
    try {
      const { data: res } = await api.get(`/projects/${pid}/status`);
      if (res.delivered && res.identity_valid) {
        toast.success(`${pid.toUpperCase()} — identité validée, projet EN LIGNE`);
      } else {
        toast.error(`${pid.toUpperCase()} — injoignable : ${res.error || "réponse invalide"}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPinging((p) => ({ ...p, [pid]: false }));
    }
  };

  if (!data) return <div className="font-mono text-xs text-neutral-500">Chargement du système…</div>;

  return (
    <div data-testid="dashboard-page" className="relative space-y-8">
      <div aria-hidden="true" className="pointer-events-none absolute -top-28 left-1/3 h-64 w-[44rem] -translate-x-1/2 rounded-full bg-[#00E5FF]/[0.05] blur-3xl" />
      <div className="relative flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-white">Tableau de bord</h1>
          <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">
            Couche universelle de connexion — {data.stats.total_projects} projets connectés
          </p>
        </div>
        <div data-testid="live-indicator" className={`flex items-center gap-2 border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] ${live ? "border-[#00E676]/40 text-[#00E676]" : "border-border text-neutral-600"}`}>
          <span className={`h-2 w-2 rounded-full ${live ? "animate-pulse bg-[#00E676]" : "bg-neutral-600"}`} />
          {live ? "Temps réel actif" : "Temps réel — reconnexion…"}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <StatTile testid="stat-projects" label="Projets" value={data.stats.total_projects} />
        <StatTile testid="stat-online" label="En ligne" value={data.stats.online} accent="text-[#00E676]" />
        <StatTile testid="stat-messages" label="Messages 24h" value={data.stats.messages_24h} accent="text-primary" />
        <StatTile testid="stat-tasks" label="Tâches actives" value={data.stats.active_tasks} accent="text-violet-300" />
        <StatTile testid="stat-alerts" label="Alertes 24h" value={data.stats.alerts_24h} accent="text-red-400" />
        <StatTile testid="stat-queue" label="File sortante" value={`${data.stats.queue_pending ?? 0}${data.stats.queue_failed ? ` · ${data.stats.queue_failed} éch.` : ""}`} accent="text-amber-300" />
      </div>

      <div>
        <h2 className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-neutral-400">Registre des projets</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {data.projects.map((p) => (
            <div
              key={p.project_id}
              data-testid={`project-card-${p.project_id}`}
              className="group border border-white/10 bg-[#121212]/90 p-5 backdrop-blur-sm transition-all duration-150 hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_0_18px_rgba(0,229,255,0.1)]"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-heading text-lg font-bold uppercase tracking-widest text-white">{p.name}</div>
                  <div className="font-mono text-[10px] text-neutral-500">{p.type}</div>
                </div>
                <StatusBadge status={p.status} />
              </div>
              <div className="mt-4 space-y-1.5 font-mono text-[11px] text-neutral-400">
                <div className="flex justify-between"><span className="text-neutral-600">Dernière comm.</span><span data-testid={`last-seen-${p.project_id}`}>{timeAgo(p.last_seen_at)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-600">Tâches actives</span><span className="text-violet-300">{p.active_tasks}</span></div>
                <div className="flex justify-between"><span className="text-neutral-600">Dernière alerte</span><span className="max-w-[130px] truncate text-red-400/80">{p.last_alert ? p.last_alert.category : "—"}</span></div>
              </div>
              {p.last_metrics && (
                <div className="mt-3 border-t border-border pt-3 font-mono text-[10px] text-neutral-500">
                  {Object.entries(p.last_metrics.metrics || {}).slice(0, 3).map(([k, v]) => (
                    <div key={k} className="flex justify-between"><span>{k}</span><span className="text-amber-300">{String(v)}</span></div>
                  ))}
                </div>
              )}
              <div className="mt-4 flex gap-2">
                <button
                  data-testid={`ping-button-${p.project_id}`}
                  onClick={() => ping(p.project_id)}
                  disabled={pinging[p.project_id]}
                  className="flex flex-1 items-center justify-center gap-2 border border-primary/40 bg-primary/5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-primary transition-colors duration-150 hover:bg-primary/15 disabled:opacity-40"
                >
                  <RadioTower size={11} /> {pinging[p.project_id] ? "Ping…" : "Ping status"}
                </button>
                <Link
                  data-testid={`detail-link-${p.project_id}`}
                  to={`/projects/${p.project_id}`}
                  className="flex items-center border border-border px-3 text-neutral-400 transition-colors duration-150 hover:border-neutral-500 hover:text-white"
                >
                  <ArrowRight size={13} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div data-testid="oracle-view-panel" className="border border-amber-500/25 bg-card shadow-[0_0_20px_rgba(245,158,11,0.06)]">
        <div className="flex items-center justify-between border-b border-amber-500/25 px-5 py-3">
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-amber-300">
            <Sparkles size={13} /> Vue Oracle — Recommandations
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600">EVENT · kind: recommendation</span>
        </div>
        <div data-testid="recommendations-list" className="divide-y divide-border">
          {recos.length === 0 && (
            <div className="px-5 py-8 text-center font-mono text-xs text-neutral-600">
              Aucune recommandation reçue pour l'instant — en attente d'Oracle
            </div>
          )}
          {recos.map((r) => (
            <div key={r.id} data-testid={`recommendation-${r.id}`} className="px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-amber-300">
                      {r.payload?.author || r.source}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-600">{r.source}</span>
                    {r.payload?.title && <span className="font-mono text-xs font-bold text-white">{r.payload.title}</span>}
                  </div>
                  <p className="mt-2 whitespace-pre-wrap font-mono text-xs leading-relaxed text-neutral-300">
                    {r.payload?.recommendation || r.payload?.text || "—"}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <PriorityBadge priority={r.priority} />
                  <span className="font-mono text-[10px] text-neutral-600">{timeAgo(r.created_at)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="border border-white/10 bg-card shadow-[0_0_16px_rgba(0,229,255,0.04)]">
          <div className="border-b border-border px-5 py-3 font-mono text-xs uppercase tracking-[0.3em] text-neutral-400">Dernières alertes</div>
          <div data-testid="recent-alerts" className="divide-y divide-border">
            {data.recent_alerts.length === 0 && <div className="px-5 py-6 font-mono text-xs text-neutral-600">Aucune alerte</div>}
            {data.recent_alerts.map((a) => (
              <div key={a.id} className="flex items-center justify-between px-5 py-3">
                <div>
                  <div className="font-mono text-xs text-white">[{a.category}] {a.message}</div>
                  <div className="font-mono text-[10px] text-neutral-600">{a.source} · {timeAgo(a.created_at)}</div>
                </div>
                <PriorityBadge priority={a.priority} />
              </div>
            ))}
          </div>
        </div>
        <div className="border border-white/10 bg-card shadow-[0_0_16px_rgba(0,229,255,0.04)]">
          <div className="border-b border-border px-5 py-3 font-mono text-xs uppercase tracking-[0.3em] text-neutral-400">Derniers messages</div>
          <div data-testid="recent-messages" className="divide-y divide-border">
            {data.recent_messages.length === 0 && <div className="px-5 py-6 font-mono text-xs text-neutral-600">Aucun message</div>}
            {data.recent_messages.map((m) => (
              <div key={m.id} className="flex items-center justify-between px-5 py-2.5">
                <div className="flex items-center gap-3">
                  <TypeBadge type={m.type} />
                  <span className="font-mono text-[11px] text-neutral-300">{m.source} → {m.destination}</span>
                </div>
                <span className="font-mono text-[10px] text-neutral-600">{fmtDate(m.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
